/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import { useTypedSelector } from '@hook/common/useStore';
import NeedSigninModal from '@component/iyagigo/modal/dialog-modal/need-signin-modal';
import ModalTemplate from '@component/common/modal/modal-template';
import {
  isPlainTextModalDataType,
  isNeedSigninModalDataType,
  isClozeExplanationModalDataType,
  isTodayStudyModalDataType,
  isTodayTestModalDataType,
  isProblemScoreModalDataType,
  isProblemExplanationModalDataType,
  isWorkbookQuitModalDataType,
  isStudyConfigurationModalDataType,
  isRemoveConfirmModalDataType,
  isSubscriptionStartModalDataType,
  isAuthCompleteModalDataType,
  isVocabularyBookStudyConfigurationModalDataType,
  isStudyPlanModalDataType,
  isStudyLanguageModalDataType,
  isGrammarStageSelectModalDataType,
  isClassroomExpressionGroupSelectModalDataType,
  isClassroomConversationGroupSelectModalDataType,
  isSpeakingStageSelectModalDataType,
} from '@datatype/iyagigo/type';
import ClozeExplanationModal from '@component/iyagigo/modal/dialog-modal/cloze-explanation-modal';
import TodayTestModal from '@component/iyagigo/modal/dialog-modal/today-test-modal';
import TodayStudyModal from '@component/iyagigo/modal/dialog-modal/today-study-modal';
import ProblemScoreModal from '@component/iyagigo/modal/dialog-modal/problem-score-modal';
import ProblemExplanationModal from '@component/iyagigo/modal/dialog-modal/problem-explanation-modal';
import ClozeStudyConfigurationModal from '@component/iyagigo/modal/dialog-modal/cloze-study-configuration-modal';
import WorkbookQuitModal from '@component/iyagigo/modal/dialog-modal/workbook-quit-modal';
import RemoveConfirmModal from '@component/iyagigo/modal/dialog-modal/remove-confirm-modal';
import SubscriptionStartModal from '@component/iyagigo/modal/dialog-modal/subscription-start-modal';
import AuthCompleteModal from '@component/iyagigo/modal/dialog-modal/auth-complete-modal';
import VocabularyBookStudyConfigurationModal from '@component/iyagigo/modal/dialog-modal/vocabulary-book-study-configuration-modal';
import StudyPlanModal from '@component/iyagigo/modal/dialog-modal/study-plan-modal';
import StudyLanguageModal from '@component/iyagigo/modal/dialog-modal/study-language-modal';
import GrammarStageSelectModal from '@component/iyagigo/modal/dialog-modal/grammar-stage-select-modal';
import ClassroomExpressionGroupSelectModal from '@component/iyagigo/modal/dialog-modal/classroom-expression-group-select-modal';
import ClassroomConversationGroupSelectModal from '@component/iyagigo/modal/dialog-modal/classroom-conversation-group-select-modal';
import SpeakingStageSelectModal from '@component/iyagigo/modal/dialog-modal/speaking-stage-select-modal';

const DIALOG_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

const PlainTextModalContainer = styled.div`
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const DialogModal = () => {
  const dialogModalData = useTypedSelector((state) => state.rootReducer?.modalReducer?.dialogModalData) || null;

  return (
    <ModalTemplate>
      {isPlainTextModalDataType(dialogModalData) && (
        <PlainTextModalContainer>
          <TextBlock text={dialogModalData.data} design={DIALOG_TEXT_DESIGN} />
        </PlainTextModalContainer>
      )}
      {isClozeExplanationModalDataType(dialogModalData) && <ClozeExplanationModal data={dialogModalData.data} />}
      {isProblemScoreModalDataType(dialogModalData) && <ProblemScoreModal data={dialogModalData.data} />}
      {isProblemExplanationModalDataType(dialogModalData) && <ProblemExplanationModal data={dialogModalData.data} />}
      {isNeedSigninModalDataType(dialogModalData) && <NeedSigninModal data={dialogModalData.data} />}
      {isTodayTestModalDataType(dialogModalData) && <TodayTestModal data={dialogModalData.data} />}
      {isTodayStudyModalDataType(dialogModalData) && <TodayStudyModal data={dialogModalData.data} />}
      {isWorkbookQuitModalDataType(dialogModalData) && <WorkbookQuitModal data={dialogModalData.data} />}
      {isRemoveConfirmModalDataType(dialogModalData) && <RemoveConfirmModal data={dialogModalData.data} />}
      {isStudyConfigurationModalDataType(dialogModalData) && (
        <ClozeStudyConfigurationModal data={dialogModalData.data} />
      )}
      {isSubscriptionStartModalDataType(dialogModalData) && <SubscriptionStartModal />}
      {isAuthCompleteModalDataType(dialogModalData) && <AuthCompleteModal data={dialogModalData.data} />}
      {isVocabularyBookStudyConfigurationModalDataType(dialogModalData) && <VocabularyBookStudyConfigurationModal />}
      {isStudyPlanModalDataType(dialogModalData) && <StudyPlanModal />}
      {isStudyLanguageModalDataType(dialogModalData) && <StudyLanguageModal />}
      {isGrammarStageSelectModalDataType(dialogModalData) && <GrammarStageSelectModal />}
      {isClassroomExpressionGroupSelectModalDataType(dialogModalData) && <ClassroomExpressionGroupSelectModal />}
      {isClassroomConversationGroupSelectModalDataType(dialogModalData) && <ClassroomConversationGroupSelectModal />}
      {isSpeakingStageSelectModalDataType(dialogModalData) && <SpeakingStageSelectModal />}
    </ModalTemplate>
  );
};

export default DialogModal;
